/* eslint-disable jsx-a11y/alt-text */
import { Typography, Box } from '@material-ui/core';
import React from 'react';
import { simpleDateFormatterData } from '../Shared/colorFormat';
import './index.css';

const moneyFormat = (value: number) => {
  if (!value && value !== 0) return '';
  if (value === 0) return '-';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

interface BudgetReportPrintProps {
  company: any;
  isRTL: boolean;
  start: any;
  end: any;
  documentTitle: string;
  isTime: boolean;
  budgetData?: any;
}

export class BudgetReportPrint extends React.PureComponent<
  BudgetReportPrintProps,
  any
> {
  render() {
    const { company, isRTL, start, end, documentTitle, isTime, budgetData } =
      this.props;

    if (!budgetData) {
      return (
        <Box style={{ textAlign: 'center', padding: 50 }}>
          <Typography>{isRTL ? 'لا توجد بيانات' : 'No data'}</Typography>
        </Box>
      );
    }

    const months = budgetData.months || [];
    const colCount = months.length + 2; // description + months + total

    const cellStyle: React.CSSProperties = {
      padding: '4px 6px',
      border: '1px solid #ccc',
      fontSize: 9,
      textAlign: 'right',
    };
    const labelStyle: React.CSSProperties = {
      ...cellStyle,
      textAlign: isRTL ? 'right' : 'left',
      fontWeight: 'normal',
    };
    const headerStyle: React.CSSProperties = {
      padding: '6px',
      border: '1px solid #ddd',
      fontSize: 9,
      textAlign: 'center',
      color: 'white',
      backgroundColor: '#4F5EB3',
      fontWeight: 'bold',
    };
    const sectionStyle: React.CSSProperties = {
      padding: '5px 6px',
      fontWeight: 'bold',
      fontSize: 10,
      border: '1px solid #ddd',
      textAlign: isRTL ? 'right' : 'left',
    };
    const totalRowStyle: React.CSSProperties = {
      ...cellStyle,
      fontWeight: 'bold',
    };

    return (
      <Box>
        <div
          style={{
            paddingLeft: 30,
            paddingRight: 30,
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          {/* Text header - first page only (not fixed) */}
          <Box
            style={{
              textAlign: 'center',
              paddingTop: 30,
              paddingBottom: 10,
            }}
          >
            {company?.logo && (
              <img
                src={company?.logo}
                alt={company?.name}
                style={{
                  maxHeight: 80,
                  objectFit: 'contain',
                  marginBottom: 10,
                }}
              />
            )}
            <Typography
              style={{ fontWeight: 'bold', fontSize: 20, marginBottom: 4 }}
            >
              {isRTL ? company?.nameAr || company?.name : company?.name}
            </Typography>
            <Typography
              style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 5 }}
            >
              {documentTitle}
            </Typography>
            {start && end && isTime && (
              <Typography
                style={{ fontSize: 13, color: '#666', marginBottom: 3 }}
              >
                {simpleDateFormatterData(start)} -{' '}
                {simpleDateFormatterData(end)}
              </Typography>
            )}
            <Typography style={{ fontSize: 12, fontWeight: 'bold' }}>
              {isRTL ? 'العملة: ريال قطري' : 'Currency: Qatari Riyal'}
            </Typography>
          </Box>

          <Box style={{ marginTop: 20 }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      ...headerStyle,
                      textAlign: isRTL ? 'right' : 'left',
                      minWidth: 120,
                    }}
                  >
                    {isRTL ? 'البيان' : 'Description'}
                  </th>
                  {months.map((m: any, i: number) => (
                    <th key={i} style={headerStyle}>
                      {isRTL ? m.labelAr : m.label}
                    </th>
                  ))}
                  <th
                    style={{
                      ...headerStyle,
                      backgroundColor: '#3a4a9f',
                    }}
                  >
                    {isRTL ? 'الإجمالي' : 'Total'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Revenue Section */}
                <tr>
                  <td
                    colSpan={colCount}
                    style={{ ...sectionStyle, backgroundColor: '#E8F0FE' }}
                  >
                    {isRTL ? 'الإيرادات' : 'Revenues'}
                  </td>
                </tr>
                {budgetData.revenue.map((acc: any, i: number) => (
                  <tr key={`rev-${i}`}>
                    <td style={labelStyle}>{isRTL ? acc.nameAr : acc.name}</td>
                    {acc.months.map((v: number, mi: number) => (
                      <td key={mi} style={cellStyle}>
                        {moneyFormat(v)}
                      </td>
                    ))}
                    <td style={totalRowStyle}>{moneyFormat(acc.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...totalRowStyle, backgroundColor: '#E8F0FE' }}>
                    {isRTL ? 'إجمالي الإيرادات' : 'Total Revenue'}
                  </td>
                  {budgetData.revenueTotals.map((v: number, i: number) => (
                    <td
                      key={i}
                      style={{ ...totalRowStyle, backgroundColor: '#E8F0FE' }}
                    >
                      {moneyFormat(v)}
                    </td>
                  ))}
                  <td style={{ ...totalRowStyle, backgroundColor: '#E8F0FE' }}>
                    {moneyFormat(budgetData.totalRevenue)}
                  </td>
                </tr>

                {/* Cost of Sales */}
                {budgetData.cost.length > 0 && (
                  <>
                    <tr>
                      <td
                        colSpan={colCount}
                        style={{
                          ...sectionStyle,
                          backgroundColor: '#FCE4EC',
                        }}
                      >
                        {isRTL ? 'تكلفة المبيعات' : 'Cost of Sales'}
                      </td>
                    </tr>
                    {budgetData.cost.map((acc: any, i: number) => (
                      <tr key={`cost-${i}`}>
                        <td style={labelStyle}>
                          {isRTL ? acc.nameAr : acc.name}
                        </td>
                        {acc.months.map((v: number, mi: number) => (
                          <td key={mi} style={cellStyle}>
                            {moneyFormat(v)}
                          </td>
                        ))}
                        <td style={totalRowStyle}>{moneyFormat(acc.total)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td
                        style={{
                          ...totalRowStyle,
                          backgroundColor: '#FCE4EC',
                        }}
                      >
                        {isRTL
                          ? 'إجمالي تكلفة المبيعات'
                          : 'Total Cost of Sales'}
                      </td>
                      {budgetData.costTotals.map((v: number, i: number) => (
                        <td
                          key={i}
                          style={{
                            ...totalRowStyle,
                            backgroundColor: '#FCE4EC',
                          }}
                        >
                          {moneyFormat(v)}
                        </td>
                      ))}
                      <td
                        style={{
                          ...totalRowStyle,
                          backgroundColor: '#FCE4EC',
                        }}
                      >
                        {moneyFormat(budgetData.totalCost)}
                      </td>
                    </tr>
                  </>
                )}

                {/* Gross Profit */}
                <tr>
                  <td style={{ ...totalRowStyle, backgroundColor: '#F0F4F8' }}>
                    {isRTL ? 'مجمل الربح' : 'Gross Profit'}
                  </td>
                  {budgetData.grossProfitMonths.map((v: number, i: number) => (
                    <td
                      key={i}
                      style={{
                        ...totalRowStyle,
                        backgroundColor: '#F0F4F8',
                        color: v < 0 ? '#f44336' : '#000',
                      }}
                    >
                      {moneyFormat(v)}
                    </td>
                  ))}
                  <td
                    style={{
                      ...totalRowStyle,
                      backgroundColor: '#F0F4F8',
                      color:
                        budgetData.totalGrossProfit < 0 ? '#f44336' : '#000',
                    }}
                  >
                    {moneyFormat(budgetData.totalGrossProfit)}
                  </td>
                </tr>

                {/* Operating Expenses */}
                <tr>
                  <td
                    colSpan={colCount}
                    style={{ ...sectionStyle, backgroundColor: '#FFF3E0' }}
                  >
                    {isRTL ? 'المصاريف التشغيلية' : 'Operating Expenses'}
                  </td>
                </tr>
                {budgetData.expenses.map((acc: any, i: number) => (
                  <tr key={`exp-${i}`}>
                    <td style={labelStyle}>{isRTL ? acc.nameAr : acc.name}</td>
                    {acc.months.map((v: number, mi: number) => (
                      <td key={mi} style={cellStyle}>
                        {moneyFormat(v)}
                      </td>
                    ))}
                    <td style={totalRowStyle}>{moneyFormat(acc.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...totalRowStyle, backgroundColor: '#FFF3E0' }}>
                    {isRTL
                      ? 'إجمالي المصاريف التشغيلية'
                      : 'Total Operating Expenses'}
                  </td>
                  {budgetData.expenseTotals.map((v: number, i: number) => (
                    <td
                      key={i}
                      style={{ ...totalRowStyle, backgroundColor: '#FFF3E0' }}
                    >
                      {moneyFormat(v)}
                    </td>
                  ))}
                  <td style={{ ...totalRowStyle, backgroundColor: '#FFF3E0' }}>
                    {moneyFormat(budgetData.totalExpenses)}
                  </td>
                </tr>

                {/* Spacer */}
                <tr>
                  <td
                    colSpan={colCount}
                    style={{ height: 8, border: 'none' }}
                  ></td>
                </tr>

                {/* Net Operating Income */}
                <tr>
                  <td
                    style={{
                      ...totalRowStyle,
                      backgroundColor: '#E8F5E9',
                      fontSize: 10,
                      padding: '6px',
                    }}
                  >
                    {isRTL ? 'صافي الدخل التشغيلي' : 'Net Operating Income'}
                  </td>
                  {budgetData.netIncomeMonths.map((v: number, i: number) => (
                    <td
                      key={i}
                      style={{
                        ...totalRowStyle,
                        backgroundColor: '#E8F5E9',
                        color: v < 0 ? '#f44336' : '#2e7d32',
                      }}
                    >
                      {moneyFormat(v)}
                    </td>
                  ))}
                  <td
                    style={{
                      ...totalRowStyle,
                      backgroundColor: '#E8F5E9',
                      color:
                        budgetData.totalNetIncome < 0 ? '#f44336' : '#2e7d32',
                    }}
                  >
                    {moneyFormat(budgetData.totalNetIncome)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>
        </div>
      </Box>
    );
  }
}
